import { MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { useThemeValue } from '@altermaind/web/ui';
import { UseTooltip, UseTooltipProps, AvailablePositions, Coordinates } from './types';

export const useTooltip = ({
  defaultVerticalPosition,
  defaultHorizontalPosition,
  horizontalTooltips,
  tooltipGap,
}: UseTooltipProps): UseTooltip => {
  const MINIMUM_FREE_SPACE_ON_TOP = 50;
  /*
   * parseFloat is used to extract the numeric value from the CSS property string.
   * If your CSS value includes a unit like px, em, or %, parseFloat will ignore the unit and return the numerical part.
   */
  const TOOLTIP_GAP = parseFloat(`${useThemeValue('space', tooltipGap)}`) || 0;
  const [position, setPosition] = useState<AvailablePositions>(
    horizontalTooltips ? defaultHorizontalPosition : defaultVerticalPosition,
  );
  const [show, setShow] = useState(false);
  const [coordinates, setCoordinates] = useState<Coordinates>({ top: 0, left: 0 });

  const handleMouseEnter: MouseEventHandler<HTMLDivElement> = () => setShow(true);
  const handleMouseLeave: MouseEventHandler<HTMLDivElement> = () => setShow(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  const handlePosition = useCallback(() => {
    if (containerRef.current && tooltipRef.current) {
      const containerPosition = containerRef.current.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(tooltipRef.current);
      const iconComputedStyle = iconRef.current
        ? window.getComputedStyle(iconRef.current)
        : undefined;

      if (!horizontalTooltips) {
        // Vertical positioning logic
        const tooltipHeight = tooltipRef.current.clientHeight || 0;
        if (defaultVerticalPosition === 'top') {
          const topSpacer =
            MINIMUM_FREE_SPACE_ON_TOP +
            tooltipHeight +
            parseFloat(computedStyle.marginBottom) +
            TOOLTIP_GAP;
          let topPosition =
            containerPosition.top -
            tooltipHeight -
            parseFloat(computedStyle.marginBottom) -
            TOOLTIP_GAP;

          if (containerPosition.top <= topSpacer) {
            setPosition('bottom');
            topPosition =
              containerPosition.top + parseFloat(iconComputedStyle?.height ?? '0') + TOOLTIP_GAP;
          } else {
            setPosition('top');
          }

          setCoordinates({
            top: topPosition + window.scrollY,
            left: containerPosition.left + containerPosition.width / 2,
          });
        } else {
          const tooltipMarginTop = parseFloat(computedStyle.marginTop) || 0;
          let topPosition =
            containerPosition.top + parseFloat(iconComputedStyle?.height ?? '0') + TOOLTIP_GAP;
          if (containerPosition.bottom + tooltipHeight + tooltipMarginTop > window.innerHeight) {
            setPosition('top');
            topPosition =
              containerPosition.top -
              parseFloat(iconComputedStyle?.height ?? '0') / 2 -
              tooltipHeight -
              tooltipMarginTop -
              TOOLTIP_GAP;
          } else {
            setPosition('bottom');
          }
          setCoordinates({
            top: topPosition + window.scrollY,
            left: containerPosition.left + containerPosition.width / 2,
          });
        }
      } else {
        // Horizontal positioning logic
        const tooltipWidth = tooltipRef.current.clientWidth || 0;
        let leftPosition: number;
        let horizontalPos: 'left' | 'right' = defaultHorizontalPosition;

        if (defaultHorizontalPosition === 'left') {
          leftPosition =
            containerPosition.left -
            tooltipWidth -
            parseFloat(computedStyle.marginRight) -
            TOOLTIP_GAP;
          // If the tooltip doesn't fit on the left side of the viewport, switch to the right
          if (leftPosition < 0) {
            horizontalPos = 'right';
            leftPosition = containerPosition.right + TOOLTIP_GAP;
          }
        } else {
          leftPosition = containerPosition.right + TOOLTIP_GAP;
          // If the tooltip doesn't fit on the right side of the viewport, switch to the left
          if (leftPosition + tooltipWidth > window.innerWidth) {
            horizontalPos = 'left';
            leftPosition =
              containerPosition.left -
              tooltipWidth -
              parseFloat(computedStyle.marginRight) -
              TOOLTIP_GAP;
          }
        }

        // The tooltip is vertically centered with respect to the container
        const topPosition =
          containerPosition.top + parseFloat(iconComputedStyle?.height ?? '0') / 2;

        setPosition(horizontalPos);
        setCoordinates({
          top: topPosition + window.scrollY,
          left: leftPosition + window.scrollX,
        });
      }
    }
  }, [
    TOOLTIP_GAP,
    defaultHorizontalPosition,
    defaultVerticalPosition,
    horizontalTooltips,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    containerRef.current?.getBoundingClientRect(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    tooltipRef.current?.getBoundingClientRect(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    iconRef.current?.getBoundingClientRect(),
  ]);

  useEffect(() => {
    handlePosition();
    window.addEventListener('scroll', handlePosition);
    window.addEventListener('resize', handlePosition);
    return () => {
      window.removeEventListener('scroll', handlePosition);
      window.removeEventListener('resize', handlePosition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalculate the position when the references change
  useEffect(() => {
    handlePosition();
  }, [handlePosition]);

  return {
    show,
    position,
    coordinates,
    containerRef,
    iconRef,
    tooltipRef,
    handleMouseEnter,
    handleMouseLeave,
  };
};
